import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, UploadCloud, Send } from "lucide-react";
import { api, SERVICE_OPTIONS, COMPANY } from "../lib/site";
import { useLang } from "../i18n/LanguageContext";

const initial = { name: "", phone: "", email: "", address: "", postal_code: "", city: "", service: "", description: "", preferred_time: "", consent: false };

export default function QuoteForm({ compact = false, defaultService }) {
  const { t } = useLang();
  const [values, setValues] = useState({ ...initial, service: defaultService || "" });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!values.consent) { toast.error(t("quote.f.consentRequired")); return; }
    setSubmitting(true);
    try {
      const imageIds = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f); fd.append("scope", "lead");
        try { const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } }); imageIds.push(r.data.id); } catch (_) { /* skip */ }
      }
      await api.post("/leads", { ...values, image_ids: imageIds });
      setSent(true); setValues(initial); setFiles([]);
      toast.success(t("quote.toastOk"));
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || t("quote.errGeneric")));
    } finally { setSubmitting(false); }
  };

  if (sent) return (
    <div className="rounded-2xl bg-white border-2 border-[#1B3626] p-7 text-center shadow-lg" data-testid="quote-success">
      <div className="mx-auto w-14 h-14 rounded-full bg-[#EEF4F0] grid place-items-center mb-4">
        <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-[#1B3626]"><path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </div>
      <div className="font-display text-2xl font-extrabold text-[#1B3626]">{t("quote.success.title")}</div>
      <p className="mt-2 text-[15px] text-[#1C1F22] leading-relaxed">{t("quote.success.body")}</p>
      <p className="mt-2 text-sm text-[#555E68]">{t("quote.success.urgent")} <a href={`tel:${COMPANY.phoneClean}`} className="font-bold text-[#1B3626] underline">{COMPANY.phone}</a>.</p>
      <button className="btn-secondary mt-6" onClick={() => setSent(false)} data-testid="quote-new-btn">{t("cta.sendMore")}</button>
    </div>
  );

  const inputCls = "w-full rounded-md border border-[#D5D0C7] bg-white px-3.5 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-[#1B3626]/40 focus:border-[#1B3626]";
  const lbl = (txt, req) => <span className="block text-sm font-semibold text-[#1C1F22] mb-1.5">{txt}{req && <span className="text-[#8C2F19]"> *</span>}</span>;

  return (
    <form onSubmit={submit} className={`grid gap-4 ${compact ? "" : "sm:gap-5"}`} data-testid="quote-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">{lbl(t("quote.f.name"), true)}<input required autoComplete="name" className={inputCls} value={values.name} onChange={onChange("name")} data-testid="q-name" /></label>
        <label className="block">{lbl(t("quote.f.phone"), true)}<input required type="tel" autoComplete="tel" inputMode="tel" className={inputCls} value={values.phone} onChange={onChange("phone")} data-testid="q-phone" /></label>
      </div>
      <label className="block">{lbl(t("quote.f.email"), true)}<input required type="email" autoComplete="email" inputMode="email" className={inputCls} value={values.email} onChange={onChange("email")} data-testid="q-email" /></label>
      <label className="block">{lbl(t("quote.f.address"), true)}<input required autoComplete="street-address" className={inputCls} value={values.address} onChange={onChange("address")} placeholder={t("quote.f.addressPh")} data-testid="q-address" /></label>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">{lbl(t("quote.f.postal"), true)}<input required autoComplete="postal-code" inputMode="numeric" className={inputCls} value={values.postal_code} onChange={onChange("postal_code")} placeholder="1776" data-testid="q-postal" /></label>
        <label className="block">{lbl(t("quote.f.city"), true)}<input required autoComplete="address-level2" className={inputCls} value={values.city} onChange={onChange("city")} placeholder="Halden" data-testid="q-city" /></label>
      </div>
      <label className="block">{lbl(t("quote.f.service"), true)}
        <select required className={inputCls} value={values.service} onChange={onChange("service")} data-testid="q-service">
          <option value="">{t("quote.f.selectService")}</option>
          {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
      <label className="block">{lbl(t("quote.f.description"), true)}
        <textarea required rows={compact ? 3 : 5} className={inputCls} placeholder={t("quote.f.descriptionPh")} value={values.description} onChange={onChange("description")} data-testid="q-desc" />
      </label>
      {!compact && (
        <label className="block">{lbl(t("quote.f.time"), false)}
          <input className={inputCls} value={values.preferred_time} onChange={onChange("preferred_time")} placeholder={t("quote.f.timePh")} data-testid="q-time" />
        </label>
      )}
      <div>
        <span className="block text-sm font-semibold text-[#1C1F22] mb-1.5">{t("quote.f.images")}</span>
        <label className="flex items-center gap-3 rounded-md border border-dashed border-[#C7BFAE] bg-[#FAF8F5] px-3.5 py-3 cursor-pointer hover:bg-[#F1EDE3]">
          <UploadCloud size={18} className="text-[#1B3626]" />
          <span className="text-sm text-[#555E68]">{files.length ? `${files.length} ${t("quote.f.imagesN")}` : t("quote.f.imagesPick")}</span>
          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="q-files" />
        </label>
      </div>
      <label className="flex items-start gap-3 text-sm text-[#383E45]">
        <input type="checkbox" className="mt-1" checked={values.consent} onChange={onChange("consent")} data-testid="q-consent" />
        <span>{t("quote.f.consent")} <a className="text-[#1B3626] underline" href="/personvern">{t("quote.f.privacy")}</a>.</span>
      </label>
      <button type="submit" disabled={submitting} className="btn-primary w-full sm:w-auto" data-testid="q-submit">
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {t("quote.f.submit")}
      </button>
    </form>
  );
}
